import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import {
  AlertTriangle, CheckCircle, XCircle, Filter,
  ChevronLeft, ChevronRight, RefreshCw
} from 'lucide-react';

const SEVERITY_CONFIG = {
  critical: { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: XCircle },
  warning: { color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: AlertTriangle },
  info: { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: AlertTriangle },
};

export default function AdminErrorsPage() {
  const { authHeaders, API } = useAdminAuth();
  const { t } = useTranslation();
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [severity, setSeverity] = useState('');
  const [resolved, setResolved] = useState('false');
  const [loading, setLoading] = useState(true);

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const [errRes, statsRes] = await Promise.all([
        axios.get(`${API}/errors`, { ...authHeaders(), params: { severity, resolved, page, limit: 20 } }),
        axios.get(`${API}/errors/stats`, authHeaders()),
      ]);
      setErrors(errRes.data.errors);
      setTotal(errRes.data.total);
      setStats(statsRes.data);
    } catch {}
    setLoading(false);
  }, [API, authHeaders, severity, resolved, page]);

  useEffect(() => { fetchErrors(); }, [fetchErrors]);

  const resolveError = async (id) => {
    try {
      await axios.put(`${API}/errors/${id}/resolve`, {}, authHeaders());
      fetchErrors();
    } catch {}
  };

  const totalPages = Math.ceil(total / 20);
  const s = stats || {};

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-errors-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">{t('admin.errorTracking.title')}</h1>
            <p className="text-sm text-slate-500">{t('admin.errorTracking.subtitle')}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchErrors} data-testid="refresh-errors-btn"
            className="text-slate-400 hover:text-white">
            <RefreshCw className="w-4 h-4 me-1" /> {t('admin.errorTracking.refresh')}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { label: t('admin.errorTracking.total'), value: s.total || 0, color: 'text-slate-300' },
            { label: t('admin.errorTracking.today'), value: s.today || 0, color: 'text-amber-400' },
            { label: t('admin.errorTracking.thisWeek'), value: s.week || 0, color: 'text-blue-400' },
            { label: t('admin.errorTracking.criticalLabel'), value: s.critical || 0, color: 'text-red-400' },
            { label: t('admin.errorTracking.unresolved'), value: s.unresolved || 0, color: 'text-orange-400' },
          ].map(st => (
            <div key={st.label} className="bg-[#0d1224] border border-slate-800/60 rounded-xl px-4 py-3">
              <p className={`text-lg font-bold ${st.color}`}>{st.value}</p>
              <p className="text-[10px] text-slate-500">{st.label}</p>
            </div>
          ))}
        </div>

        {/* Top Endpoints */}
        {s.top_endpoints?.length > 0 && (
          <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-4" data-testid="top-error-endpoints">
            <h2 className="text-sm font-semibold text-white mb-3">{t('admin.errorTracking.topEndpoints')}</h2>
            <div className="space-y-2">
              {s.top_endpoints.slice(0, 5).map((ep, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <code className="text-slate-300 bg-slate-800/50 px-2 py-1 rounded font-mono text-[11px]">{ep.endpoint}</code>
                  <span className="text-red-400 font-medium">{ep.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 flex-wrap items-center">
          <Filter className="w-4 h-4 text-slate-500" />
          {[
            { val: '', label: t('admin.errorTracking.all') },
            { val: 'critical', label: t('admin.errorTracking.critical') },
            { val: 'warning', label: t('admin.errorTracking.warning') },
          ].map(sev => (
            <Button key={sev.val} size="sm" variant="ghost"
              data-testid={`filter-severity-${sev.val || 'all'}`}
              onClick={() => { setSeverity(sev.val); setPage(1); }}
              className={`text-xs h-7 ${severity === sev.val ? 'bg-slate-800 text-white' : 'text-slate-400'}`}>
              {sev.label}
            </Button>
          ))}
          <div className="w-px h-4 bg-slate-800 mx-1" />
          {[
            { val: 'false', label: t('admin.errorTracking.unresolved') },
            { val: 'true', label: t('admin.errorTracking.resolved') },
            { val: '', label: t('admin.errorTracking.all') },
          ].map(f => (
            <Button key={`r-${f.val}`} size="sm" variant="ghost"
              onClick={() => { setResolved(f.val); setPage(1); }}
              className={`text-xs h-7 ${resolved === f.val ? 'bg-slate-800 text-white' : 'text-slate-400'}`}>
              {f.label}
            </Button>
          ))}
        </div>

        {/* Error List */}
        <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : errors.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{t('admin.errorTracking.noErrors')}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800/40">
              {errors.map(err => {
                const sConfig = SEVERITY_CONFIG[err.severity] || SEVERITY_CONFIG.info;
                const SevIcon = sConfig.icon;
                return (
                  <div key={err.id} className="px-4 py-3 hover:bg-slate-800/20 transition-colors" data-testid={`error-row-${err.id}`}>
                    <div className="flex items-start gap-3">
                      <SevIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        err.severity === 'critical' ? 'text-red-400' : 'text-amber-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <code className="text-xs text-slate-200 font-mono bg-slate-800/50 px-1.5 py-0.5 rounded">
                            {err.method} {err.endpoint}
                          </code>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${sConfig.color}`}>
                            {err.status_code}
                          </span>
                          {err.is_resolved && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/10 text-emerald-400">
                              {t('admin.errorTracking.resolved')}
                            </span>
                          )}
                        </div>
                        {err.error_message && (
                          <p className="text-xs text-slate-500 mt-1 truncate">{err.error_message}</p>
                        )}
                        <p className="text-[10px] text-slate-600 mt-1">{new Date(err.timestamp).toLocaleString()}</p>
                      </div>
                      {!err.is_resolved && (
                        <Button size="sm" variant="ghost" onClick={() => resolveError(err.id)}
                          data-testid={`resolve-error-${err.id}`}
                          className="text-emerald-500 hover:bg-emerald-500/10 text-xs h-7 flex-shrink-0">
                          <CheckCircle className="w-3 h-3 me-1" /> {t('admin.errorTracking.resolve')}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800/60">
              <span className="text-xs text-slate-500">{t('admin.tenants.page')} {page} {t('admin.tenants.of')} {totalPages}</span>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="text-slate-400 h-7 w-7 p-0"><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  className="text-slate-400 h-7 w-7 p-0"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
